import type { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import type { ColumnDef, OnChangeFn, RowSelectionState, SortingState } from '@plato/ui/components/data-table';
import { DataTable } from '@plato/ui/components/data-table';
import { useTranslation } from 'react-i18next';
import type {
    CustomDateRange,
    ExplorerViewFilters,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';
import type { ExplorerTableData } from '../hooks/use-configurable-explorer-columns.tsx';
import { useExplorerTotalSummary } from '../hooks/use-explorer-total-summary.tsx';

type ExplorerTableProps = {
    data: ExplorerTableData[];
    columns: ColumnDef<ExplorerTableData>[];
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    isFetching: boolean;
    hasNextPage?: boolean;
    isLoading: boolean;
    filters: ExplorerViewFilters;
    window: VisibilityWindow;
    billingStatus: SalesMetricBasis;
    customDateRange: CustomDateRange | undefined;
    rowSelection: RowSelectionState;
    setRowSelection: OnChangeFn<RowSelectionState>;
    sorting: SortingState;
    sortChangedInInterface: OnChangeFn<SortingState>;
};

export function ExplorerDataTable({
    data,
    columns,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    rowSelection,
    setRowSelection,
    sorting,
    sortChangedInInterface,
}: ExplorerTableProps) {
    const { t } = useTranslation();
    const { summaryHeader } = useExplorerTotalSummary();

    return (
        <DataTable
            emptyLabel={t('empty_table__no_results')}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            data={data}
            columns={columns}
            enableMultiRowSelection={true}
            manualSorting
            onRowSelectionChange={setRowSelection}
            onSortingChange={sortChangedInInterface}
            state={{ rowSelection, sorting }}
            summaryHeader={summaryHeader}
            dimNonSelectedRows
        />
    );
}
